/**
 * CIRCULAR FILTERING - the whole reason the sea can loop without a click.
 *
 * -- THE DEFECT THIS FILE EXISTS TO MAKE IMPOSSIBLE ------------------------------
 *
 * A looping ambience is a buffer whose last sample is followed by its first, forever. If the
 * signal does not join up there, every lap produces a step discontinuity - broadband energy in
 * one sample - and a step is exactly what a click is. It is the single most likely defect in a
 * looped bed, it is inaudible while you audition the first lap, and it is unmistakable once you
 * have heard it, which is a bad combination for something a player leaves running for hours.
 *
 * The usual fixes are both compromises. Crossfading the tail into the head fixes the amplitude
 * but doubles the texture across the overlap, and the overlap itself becomes an audible
 * periodic event. Fading to silence at both ends is not a loop at all.
 *
 * -- WHAT THIS DOES INSTEAD: MAKE THE SIGNAL GENUINELY PERIODIC ------------------
 *
 * Start from white noise of length N and treat it as a signal on a RING rather than a line:
 * sample -1 is sample N-1. Filter it on that ring, and the output is periodic with period N by
 * construction, for any N, because every output sample was computed from the same circular
 * neighbourhood a listener will actually hear it in. There is no seam to fix because there is
 * no seam: the loop point is an ordinary sample boundary and nothing about it is special.
 *
 * -- WHY A ONE-POLE, AND WHY THE RING IS EXACT RATHER THAN APPROXIMATE -----------
 *
 * A one-pole lowpass is y[n] = a*y[n-1] + (1-a)*x[n], with a = exp(-2*pi*fc/sr). To run it on
 * the ring you need the value y[-1] that the filter would settle to after going round forever:
 *
 *     y[-1] = (1-a) * SUM over j >= 1 of a^(j-1) * x[(N-j) mod N]
 *
 * That series converges geometrically, so truncating it after W terms leaves a relative error
 * of a^W. `warmupFor` picks W so a^W < 1e-15 - below the resolution of the double the sample is
 * held in, and about eleven orders of magnitude below the least significant bit of 16-bit
 * audio. The periodicity is therefore not "close enough by ear", it is exact to the arithmetic,
 * and `measure.ts` reports the residual rather than assuming it.
 *
 * Cost is the warm-up length, once per stage: 1,466 samples at 180 Hz and 8,795 at 30 Hz on a
 * 48 kHz buffer of about 500,000 - under 2% of the pass either way.
 *
 * -- WHY NO RESONANCE ANYWHERE IN THIS FILE --------------------------------------
 *
 * There is no biquad here and that is a design decision, not an omission. A resonant filter on
 * noise produces a PITCH, and a pitch you cannot get away from is the fastest route to the mute
 * button in a game whose bed plays for hours. Every shape in this lane is built from cascaded
 * one-poles, which have no peak at all: they can tilt a spectrum but they cannot ring.
 */

/** exp(-2*pi*fc/sr): how much of the previous output a one-pole at `fc` keeps. */
export function poleFor(cutoffHz: number, sampleRate: number): number {
  const f = Math.max(0.01, Math.min(cutoffHz, sampleRate * 0.45));
  return Math.exp((-2 * Math.PI * f) / sampleRate);
}

/**
 * How many samples of run-up make the ring exact.
 *
 * a^W < 1e-15 solves to W > 15*ln(10) / -ln(a). Capped at the buffer length, because past one
 * full lap the sum has seen every sample there is and more terms add nothing.
 */
export function warmupFor(a: number, length: number): number {
  if (a <= 0) return 1;
  const decay = -Math.log(a);
  if (!Number.isFinite(decay) || decay <= 0) return length;
  return Math.min(length, Math.ceil((15 * Math.LN10) / decay) + 8);
}

/**
 * One pole of lowpass, applied around the ring, in place.
 *
 * 6 dB per octave above `cutoffHz`. Call it twice for 12, three times for 18; cascading is
 * how every shape in `sea.ts` is built, because a cascade of one-poles has a soft knee and no
 * peak, and a peak is a pitch.
 */
export function circularLowpass(buf: Float32Array, cutoffHz: number, sampleRate: number): void {
  const n = buf.length;
  if (n === 0) return;
  const a = poleFor(cutoffHz, sampleRate);
  const b = 1 - a;
  const w = warmupFor(a, n);
  // Run up on the tail so the state entering sample 0 is the state that leaves sample N-1.
  let y = buf[n - 1] as number;
  for (let i = n - w; i < n; i++) y = a * y + b * (buf[i] as number);
  for (let i = 0; i < n; i++) {
    y = a * y + b * (buf[i] as number);
    buf[i] = y;
  }
}

/**
 * One pole of highpass around the ring, in place: the signal minus its own lowpass.
 *
 * Periodic because the lowpass is, and a difference of two periodic signals of the same period
 * is periodic. 6 dB per octave below `cutoffHz`. Used to take out sub-bass that costs headroom
 * and reaches no laptop speaker, never to shape anything anybody hears as a note.
 */
export function circularHighpass(buf: Float32Array, cutoffHz: number, sampleRate: number): void {
  const n = buf.length;
  if (n === 0) return;
  const a = poleFor(cutoffHz, sampleRate);
  const b = 1 - a;
  const w = warmupFor(a, n);
  let y = buf[n - 1] as number;
  for (let i = n - w; i < n; i++) y = a * y + b * (buf[i] as number);
  for (let i = 0; i < n; i++) {
    const x = buf[i] as number;
    y = a * y + b * x;
    buf[i] = x - y;
  }
}

/**
 * THE SAME TWO FILTERS ON A LINE RATHER THAN A RING, for signals that are not loops.
 *
 * A one-shot starts at silence and ends at silence, so there is no joint to keep continuous and
 * the run-up above would only smear the attack - which for a plip is the entire sound. These
 * start from zero state, which is correct for a signal that starts from zero.
 */
export function lowpassLinear(buf: Float32Array, cutoffHz: number, sampleRate: number): void {
  const a = poleFor(cutoffHz, sampleRate);
  const b = 1 - a;
  let y = 0;
  for (let i = 0; i < buf.length; i++) {
    y = a * y + b * (buf[i] as number);
    buf[i] = y;
  }
}

export function highpassLinear(buf: Float32Array, cutoffHz: number, sampleRate: number): void {
  const a = poleFor(cutoffHz, sampleRate);
  const b = 1 - a;
  let y = 0;
  for (let i = 0; i < buf.length; i++) {
    const x = buf[i] as number;
    y = a * y + b * x;
    buf[i] = x - y;
  }
}

/**
 * A PERIODIC ENVELOPE, built only from whole harmonics of the buffer length.
 *
 * The sea has to breathe or it is a hiss, and a breath is an amplitude envelope. An envelope
 * drawn from a noise source would break the ring; one built from sinusoids at exactly k cycles
 * per buffer cannot, for any k, because k cycles per buffer is what periodic means.
 *
 * `partials` are those k values. Keeping them small and mutually awkward (3, 5, 8, 13) is what
 * stops the result reading as one regular pulse: the sum has no common sub-period shorter than
 * the buffer, so the breath is uneven the way a real swell is. The output spans [1-depth, 1].
 */
export function periodicEnvelope(
  length: number,
  partials: readonly number[],
  phases: readonly number[],
  depth: number,
): Float32Array {
  const env = new Float32Array(length);
  if (length === 0) return env;

  // CONTROL POINTS, NOT A SINE PER SAMPLE, and the reason is a measurement rather than a habit.
  //
  // The first version of this function called Math.sin once per partial per sample: about eight
  // million transcendental calls to build one sea. It MEASURED at 774 ms of blocked main thread
  // on the player's first click - three quarters of a second of frozen page in order to start a
  // sound - and it was the whole of that cost.
  //
  // The envelope is the slowest-moving thing in this entire lane. Its fastest partial is 13
  // cycles across 11 seconds, about 1.2 Hz. Sampling it every 256 samples is 187 Hz, a hundred
  // and fifty times its own top frequency, and straight-line interpolation between those points
  // is wrong by about two parts in ten thousand - a smooth error, not a discontinuity, and four
  // orders of magnitude below anything a listener could reach.
  //
  // PERIODICITY SURVIVES EXACTLY, which is the one property that could not be traded for speed:
  // the wrapping control point is ASSIGNED from the first rather than recomputed, so the final
  // segment lands on env[0] to the bit however the length happens to divide.
  const stride = 256;
  const segments = Math.max(1, Math.ceil(length / stride));
  const ctrl = new Float64Array(segments + 1);
  for (let p = 0; p < segments; p++) {
    const i = p * stride;
    let s = 0;
    for (let q = 0; q < partials.length; q++) {
      const k = partials[q] as number;
      // Weighting by 1/k puts most of the movement in the slowest partial, which is what
      // makes it read as a swell rather than as a tremolo.
      s += Math.sin((2 * Math.PI * k * i) / length + (phases[q] ?? 0)) / k;
    }
    ctrl[p] = s;
  }
  ctrl[segments] = ctrl[0] as number;

  let lo = Infinity;
  let hi = -Infinity;
  for (let p = 0; p < segments; p++) {
    const start = p * stride;
    const end = Math.min(length, start + stride);
    const a = ctrl[p] as number;
    const b = ctrl[p + 1] as number;
    const span = end - start;
    for (let i = start; i < end; i++) {
      const v = a + ((b - a) * (i - start)) / span;
      env[i] = v;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
  }

  const range = hi - lo;
  const d = Math.max(0, Math.min(1, depth));
  for (let i = 0; i < length; i++) {
    const unit = range > 0 ? ((env[i] as number) - lo) / range : 1;
    env[i] = 1 - d + d * unit;
  }
  return env;
}

/** Scale a buffer so its RMS is exactly `target`. Returns the gain applied. */
export function normaliseRms(buf: Float32Array, target: number): number {
  let sum = 0;
  for (let i = 0; i < buf.length; i++) {
    const v = buf[i] as number;
    sum += v * v;
  }
  const rms = Math.sqrt(sum / Math.max(1, buf.length));
  if (rms <= 0) return 0;
  const g = target / rms;
  for (let i = 0; i < buf.length; i++) buf[i] = (buf[i] as number) * g;
  return g;
}
